import type { Expense, ExpectedExpense } from '@otw/models'
import { expectedExpenseConverter } from '@otw/models/converters'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { collection, deleteDoc, doc, getDocs, updateDoc, addDoc } from '@firebase/firestore'
import { deleteObject, getDownloadURL, ref, uploadBytesResumable } from '@firebase/storage'
import { db, storage } from 'config/firebase'
import { useAuth } from 'contexts/AuthContext'
import { useSnackbar } from '@/hooks/common/useSnackbar'
import { useExpenses } from './useExpenses'

const queryKey = ['expected-expenses'] as const

const getAttachmentPaths = (expected: Partial<ExpectedExpense>): string[] => {
  const paths = Array.isArray(expected.attachmentPaths)
    ? expected.attachmentPaths.filter((path): path is string => typeof path === 'string' && path.length > 0)
    : []
  if (paths.length > 0) return paths
  return expected.attachmentPath ? [expected.attachmentPath] : []
}

const getAttachmentURLs = (expected: Partial<ExpectedExpense>): string[] => {
  const urls = Array.isArray(expected.attachmentURLs)
    ? expected.attachmentURLs.filter((url): url is string => typeof url === 'string' && url.length > 0)
    : []
  if (urls.length > 0) return urls
  return expected.attachmentURL ? [expected.attachmentURL] : []
}

const downloadStorageFile = async (storagePath: string, fallbackName: string): Promise<File> => {
  const downloadUrl = await getDownloadURL(ref(storage, storagePath))
  const response = await fetch(downloadUrl)
  if (!response.ok) throw new Error(`Failed to download ${fallbackName}`)
  const blob = await response.blob()
  const fileName = storagePath.split('/').pop() || fallbackName
  return new File([blob], fileName, { type: blob.type || 'application/pdf' })
}

const uploadExpectedAttachments = async (expectedId: string, files: File[]) => {
  const uploaded = await Promise.all(
    files.map(async (file, index) => {
      const safeFileName = file.name.replace(/\s+/g, '_')
      const filePath = `expected_expenses/${expectedId}/${Date.now()}-${index}-${safeFileName}`
      const storageRef = ref(storage, filePath)
      const uploadTask = await uploadBytesResumable(storageRef, file)
      const downloadURL = await getDownloadURL(uploadTask.ref)
      return { attachmentPath: filePath, attachmentURL: downloadURL }
    }),
  )
  return {
    attachmentPaths: uploaded.map(item => item.attachmentPath),
    attachmentURLs: uploaded.map(item => item.attachmentURL),
  }
}

export type UpdateExpectedExpenseInput = {
  expectedData: Pick<ExpectedExpense, 'id'> & Partial<Omit<ExpectedExpense, 'id' | 'created_by' | 'created_at'>>
  attachmentFiles?: File[] | null
  silent?: boolean
}

export const useExpectedExpenses = () => {
  const { userInfo } = useAuth()
  const { showSuccess, showError, showInfo } = useSnackbar()
  const queryClient = useQueryClient()
  const {
    addExpense,
    importRepairShopInvoice,
    isImportingRepairShopInvoice,
  } = useExpenses({ options: { dontShowUpdates: true } })
  const expectedCollection = collection(db, 'expected_expenses').withConverter(expectedExpenseConverter)

  const {
    data: expectedExpenses = [],
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey,
    queryFn: async () => {
      const snapshot = await getDocs(expectedCollection)
      const values = snapshot.docs.map(item => item.data())

      // Server-created estimate copies do not have client download URLs. Resolve them
      // for display without persisting short-lived URLs back to Firestore.
      return Promise.all(
        values.map(async expected => {
          const paths = getAttachmentPaths(expected)
          if (paths.length === 0 || (expected.attachmentURLs?.length ?? 0) > 0) return expected
          const urls = await Promise.all(
            paths.map(async path => {
              try {
                return await getDownloadURL(ref(storage, path))
              } catch {
                return ''
              }
            }),
          )
          const validUrls = urls.filter(Boolean)
          return {
            ...expected,
            attachmentURL: validUrls[0] ?? null,
            attachmentURLs: validUrls.length > 0 ? validUrls : null,
          }
        }),
      )
    },
    staleTime: 1000 * 60 * 5,
    refetchInterval: query => {
      const data = query.state.data as ExpectedExpense[] | undefined
      return data?.some(item => item.reviewStatus === 'processing') ? 4000 : false
    },
  })

  const updateExpectedExpenseMutation = useMutation({
    mutationFn: async ({ expectedData, attachmentFiles }: UpdateExpectedExpenseInput) => {
      if (!userInfo) throw new Error('User must be authenticated to update expected expenses')
      const current = expectedExpenses.find(item => item.id === expectedData.id)
      if (!current) throw new Error('Expected expense not found')

      const merged: ExpectedExpense = {
        ...current,
        ...expectedData,
        updated_at: new Date(),
        updated_by: userInfo.id || userInfo.email || '',
      }

      const currentPaths = getAttachmentPaths(current)
      const currentURLs = getAttachmentURLs(current)
      const requestedExistingPaths = Array.isArray(expectedData.attachmentPaths)
        ? expectedData.attachmentPaths.filter((path): path is string => typeof path === 'string' && path.length > 0)
        : currentPaths
      const requestedExistingURLs = Array.isArray(expectedData.attachmentURLs)
        ? expectedData.attachmentURLs.filter((url): url is string => typeof url === 'string' && url.length > 0)
        : currentURLs

      if (attachmentFiles?.length) {
        const attachmentInfo = await uploadExpectedAttachments(expectedData.id, attachmentFiles)
        const pathsToDelete = currentPaths.filter(path => !requestedExistingPaths.includes(path))
        await Promise.all(
          pathsToDelete.map(async path => {
            try {
              await deleteObject(ref(storage, path))
            } catch (storageError) {
              console.warn(`Unable to delete expected expense attachment ${path}`, storageError)
            }
          }),
        )
        const finalPaths = [...requestedExistingPaths, ...attachmentInfo.attachmentPaths]
        const finalURLs = [...requestedExistingURLs, ...attachmentInfo.attachmentURLs]
        merged.attachmentPath = finalPaths[0] ?? null
        merged.attachmentURL = finalURLs[0] ?? null
        merged.attachmentPaths = finalPaths
        merged.attachmentURLs = finalURLs
      } else if (expectedData.attachmentPath === null || expectedData.attachmentPaths === null) {
        await Promise.all(
          currentPaths.map(async path => {
            try {
              await deleteObject(ref(storage, path))
            } catch (storageError) {
              console.warn(`Unable to delete expected expense attachment ${path}`, storageError)
            }
          }),
        )
        merged.attachmentPath = null
        merged.attachmentURL = null
        merged.attachmentPaths = null
        merged.attachmentURLs = null
      } else {
        merged.attachmentPaths = getAttachmentPaths(merged)
        merged.attachmentURLs = getAttachmentURLs(merged)
        merged.attachmentPath = merged.attachmentPaths[0] ?? null
        merged.attachmentURL = merged.attachmentURLs[0] ?? null
      }

      const { id: _id, ...firestoreData } = expectedExpenseConverter.toFirestore(merged)
      await updateDoc(doc(expectedCollection, merged.id), firestoreData)
      return merged
    },
    onSuccess: (result, variables) => {
      queryClient.setQueryData<ExpectedExpense[]>(queryKey, old =>
        old?.map(item => (item.id === result.id ? result : item)) ?? [result],
      )
      queryClient.invalidateQueries({ queryKey })
      if (!variables.silent) {
        showSuccess('Expected expense updated')
      }
    },
    onError: (mutationError: Error, variables) => {
      if (!variables.silent) {
        showError(`Error updating expected expense: ${mutationError.message}`)
      } else {
        console.error('Error updating expected expense:', mutationError)
      }
    },
  })

  const removeExpectedExpense = async (expected: ExpectedExpense) => {
    await Promise.all(
      getAttachmentPaths(expected).map(async path => {
        try {
          await deleteObject(ref(storage, path))
        } catch (storageError) {
          // A missing attachment should not prevent deleting/converting the Firestore row.
          console.warn(`Unable to delete expected expense attachment ${path}`, storageError)
        }
      }),
    )
    await deleteDoc(doc(expectedCollection, expected.id))
  }

  const deleteExpectedExpenseMutation = useMutation({
    mutationFn: async (id: string) => {
      const expected = expectedExpenses.find(item => item.id === id)
      if (!expected) throw new Error('Expected expense not found')
      await removeExpectedExpense(expected)
      return id
    },
    onSuccess: id => {
      queryClient.setQueryData<ExpectedExpense[]>(queryKey, old => old?.filter(item => item.id !== id) ?? [])
      queryClient.invalidateQueries({ queryKey })
      showSuccess('Expected expense deleted')
    },
    onError: (mutationError: Error) => showError(`Error deleting expected expense: ${mutationError.message}`),
  })

  const convertExpectedToExpenseMutation = useMutation({
    mutationFn: async (expectedId: string): Promise<string> => {
      const expected = expectedExpenses.find(item => item.id === expectedId)
      if (!expected) throw new Error('Expected expense not found')
      if (expected.reviewStatus !== 'reviewed') {
        throw new Error('Review and save the expected expense before converting it')
      }

      const files = await Promise.all(
        getAttachmentPaths(expected).map(path => downloadStorageFile(path, 'estimate.pdf')),
      )
      const expenseData: Omit<Expense, 'id' | 'created_by' | 'created_at' | 'updated_at' | 'updated_by'> = {
        category: expected.category || 'Repairs',
        amount: expected.amount,
        paid: false,
        paidAt: null,
        plannedPaymentWindow: null,
        creditCardCharge: null,
        discountType: expected.discountType ?? null,
        discountValue: expected.discountValue ?? null,
        description: expected.description,
        date: expected.date,
        customCategory: expected.customCategory,
        vehicleId: expected.vehicleId,
        transportCompanyId: null,
        invoiceNumber: expected.invoiceNumber,
        vendorCategory: expected.vendorCategory,
        businessName: expected.businessName,
        attachmentPath: null,
        attachmentURL: null,
        attachmentPaths: null,
        attachmentURLs: null,
        attachmentRotations: expected.attachmentRotations,
        repairSubcategories: expected.repairSubcategories,
        customRepairSubcategory: expected.customRepairSubcategory,
        repairPartsAmount: expected.repairPartsAmount,
        repairLaborAmount: expected.repairLaborAmount,
        repairFeesAmount: expected.repairFeesAmount,
        repairTaxAmount: expected.repairTaxAmount,
        repairShopId: expected.repairShopId,
        repairTruckId: expected.repairTruckId,
        sourceRepairInvoiceStoragePath: null,
        sourceRepairTruckId: expected.sourceRepairTruckId,
        partName: null,
        reimbursementSubcategory: null,
        lineItems: expected.lineItems,
        reviewStatus: null,
        awaitingExtraction: null,
        extractedData: expected.extractedData,
        extractionModel: expected.extractionModel,
        extractionError: null,
        suggestedCategory: expected.suggestedCategory,
      }

      const created = await addExpense({ expenseData, attachmentFiles: files })
      await removeExpectedExpense(expected)
      return created.id
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey })
      queryClient.invalidateQueries({ queryKey: ['expenses'] })
      showSuccess('Expected expense converted to an expense')
    },
    onError: (mutationError: Error) => showError(`Error converting expected expense: ${mutationError.message}`),
  })

  const pullOverInvoiceMutation = useMutation({
    mutationFn: async ({
      expectedId,
      invoiceStoragePath,
    }: {
      expectedId: string
      invoiceStoragePath?: string
    }): Promise<string> => {
      const expected = expectedExpenses.find(item => item.id === expectedId)
      if (!expected) throw new Error('Expected expense not found')
      const storagePath = invoiceStoragePath || expected.pendingInvoiceStoragePath
      if (!storagePath) throw new Error('No invoice is available for this expected expense')
      if (!expected.repairTruckId) throw new Error('Expected expense is not linked to a repair visit')

      const expenseId = await importRepairShopInvoice({
        storagePath,
        repairTruckId: expected.repairTruckId,
        repairShopId: expected.repairShopId,
        vehicleId: expected.vehicleId,
        note: expected.description || undefined,
      })
      await removeExpectedExpense(expected)
      return expenseId
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey })
      queryClient.invalidateQueries({ queryKey: ['expenses'] })
      showInfo('Reading repair shop invoice…')
    },
    onError: (mutationError: Error) => showError(`Error pulling over invoice: ${mutationError.message}`),
  })

  const importEstimatesMutation = useMutation({
    mutationFn: async (files: File[]) => {
      if (!userInfo) throw new Error('User must be authenticated to import estimates')

      const createdIds: string[] = []
      for (const file of files) {
        const now = new Date()
        const pendingExpected: Omit<ExpectedExpense, 'id'> = {
          category: 'Repairs',
          amount: 0,
          description: '',
          date: now,
          customCategory: null,
          businessName: null,
          invoiceNumber: null,
          vendorCategory: null,
          vehicleId: null,
          repairShopId: null,
          repairTruckId: null,
          sourceRepairEstimateStoragePath: null,
          sourceRepairTruckId: null,
          pendingInvoiceStoragePath: null,
          attachmentPath: null,
          attachmentURL: null,
          attachmentPaths: null,
          attachmentURLs: null,
          attachmentRotations: null,
          repairSubcategories: null,
          customRepairSubcategory: null,
          repairPartsAmount: null,
          repairLaborAmount: null,
          repairFeesAmount: null,
          repairTaxAmount: null,
          discountType: null,
          discountValue: null,
          lineItems: null,
          reviewStatus: 'processing',
          awaitingExtraction: true,
          extractedData: null,
          extractionModel: null,
          extractionError: null,
          suggestedCategory: null,
          created_by: userInfo.id || userInfo.email || '',
          created_at: now,
          updated_at: null,
          updated_by: null,
        }

        const docRef = await addDoc(expectedCollection, pendingExpected)
        const { attachmentPaths, attachmentURLs } = await uploadExpectedAttachments(docRef.id, [file])
        await updateDoc(doc(expectedCollection, docRef.id), {
          attachmentPath: attachmentPaths[0] ?? null,
          attachmentURL: attachmentURLs[0] ?? null,
          attachmentPaths,
          attachmentURLs,
        })
        createdIds.push(docRef.id)
      }
      return createdIds
    },
    onSuccess: ids => {
      queryClient.invalidateQueries({ queryKey })
      showInfo(`Reading ${ids.length} estimate${ids.length === 1 ? '' : 's'}…`)
    },
    onError: (mutationError: Error) => showError(`Error importing estimate: ${mutationError.message}`),
  })

  const totalExpectedExpenses = expectedExpenses.reduce((sum, item) => sum + item.amount, 0)

  return {
    expectedExpenses,
    totalExpectedExpenses,
    isLoading,
    isError,
    error,
    refetchExpectedExpenses: refetch,
    updateExpectedExpense: updateExpectedExpenseMutation.mutateAsync,
    deleteExpectedExpense: deleteExpectedExpenseMutation.mutateAsync,
    convertExpectedToExpense: convertExpectedToExpenseMutation.mutateAsync,
    pullOverInvoice: (expectedId: string, invoiceStoragePath?: string) =>
      pullOverInvoiceMutation.mutateAsync({ expectedId, invoiceStoragePath }),
    importEstimates: importEstimatesMutation.mutateAsync,
    isUpdating: updateExpectedExpenseMutation.isPending,
    isDeleting: deleteExpectedExpenseMutation.isPending,
    isConverting: convertExpectedToExpenseMutation.isPending,
    isPullingInvoice: pullOverInvoiceMutation.isPending || isImportingRepairShopInvoice,
    isImportingEstimates: importEstimatesMutation.isPending,
  }
}
